import React, { useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { baseURL } from "../../App";
import "./admin.css";

const AboutAdmin = () => {
  const [aboutData, setAboutData] = useState({
    clients: 0,
    projects: 0,
    workExperience: 0,
    currentStatus: "",
    identity: "",
    description: { en: "", fr: "" },
    image: "",
    _id: null,
  });

  const [allAboutData, setAllAboutData] = useState([]);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const API_URL = `${baseURL}/api/about`;
  const token = Cookies.get("token");

  useEffect(() => {
    const fetchAboutData = async () => {
      try {
        const response = await axios.get(API_URL, { withCredentials: true }); //ensure cookies are sent with the request
        setAllAboutData(response.data);
      } catch (err) {
        console.error("Error fetching About data:", err);
      }
    };

    fetchAboutData();
  }, [API_URL]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setAboutData((prevData) => ({
      ...prevData,
      [name]: name === "image" ? files[0] : value,
    }));
  };

  const handleDescriptionChange = (e) => {
    const { name, value } = e.target;
    setAboutData((prevData) => ({
      ...prevData,
      description: {
        ...prevData.description,
        [name]: value,
      },
    }));
  };

  const handleSelectChange = (e) => {
    const selectedId = e.target.value;
    if (selectedId === "new") {
      resetForm();
    } else {
      const selectedData =
        allAboutData.find((item) => item._id === selectedId) || {};
      setAboutData(selectedData);
    }
  };

  const handleCreate = async () => {
    const data = new FormData();
    data.append("clients", Number(aboutData.clients));
    data.append("projects", Number(aboutData.projects));
    data.append("workExperience", Number(aboutData.workExperience));
    data.append("currentStatus", aboutData.currentStatus);
    data.append("identity", aboutData.identity);
    data.append("description[en]", aboutData.description.en);
    data.append("description[fr]", aboutData.description.fr);

    if (aboutData.image instanceof File) {
      data.append("file", aboutData.image);
    }

    console.log("About Data:", aboutData, data);
    try {
      const response = await axios.post(API_URL, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true, // Ensure cookies are sent with the request
      });

      setConfirmationMessage("Data created successfully!");
      setAllAboutData((prev) => [...prev, response.data]);
      resetForm();
    } catch (err) {
      const msg = err.response?.data?.message;
      setConfirmationMessage(
        Array.isArray(msg) ? msg.join(", ") : msg || "Error creating data.",
      );
      console.error("Create Error:", err.response?.data);
    }
  };

  const handleUpdate = async () => {
    if (!aboutData._id) {
      // console.log("No ID selected for update.");
      return;
    }

    const data = new FormData();
    data.append("clients", Number(aboutData.clients));
    data.append("projects", Number(aboutData.projects));
    data.append("workExperience", Number(aboutData.workExperience));
    data.append("currentStatus", aboutData.currentStatus);
    data.append("identity", aboutData.identity);
    data.append("description[en]", aboutData.description.en);
    data.append("description[fr]", aboutData.description.fr);

    if (aboutData.image instanceof File) {
      data.append("file", aboutData.image);
    }
    console.log("About Data:", aboutData, data);
    try {
      // Use PATCH and multipart/form-data for updates
      const response = await axios.patch(`${API_URL}/${aboutData._id}`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true, // Ensure cookies are sent with the request
      });

      setConfirmationMessage("Data updated successfully!");
      setAllAboutData((prev) =>
        prev.map((item) => (item._id === aboutData._id ? response.data : item)),
      );
    } catch (err) {
      const msg = err.response?.data?.message;
      setConfirmationMessage(
        Array.isArray(msg) ? msg.join(", ") : msg || "Error updating data.",
      );
      console.error("Update Error:", err.response?.data);
    }
  };

  const handleDelete = async () => {
    if (!aboutData._id) return;

    try {
      await axios.delete(`${API_URL}/${aboutData._id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });
      setConfirmationMessage("Data deleted successfully!");
      setAllAboutData((prev) =>
        prev.filter((item) => item._id !== aboutData._id),
      );
      resetForm();
    } catch (err) {
      console.error("Error deleting About data:", err);
      setConfirmationMessage(err.message);
    }
  };

  const resetForm = () => {
    setAboutData({
      clients: 0,
      projects: 0,
      workExperience: 0,
      currentStatus: "",
      identity: "",
      description: { en: "", fr: "" },
      image: "",
      _id: null,
    });
  };

  const createDisabled = aboutData._id !== null; // Disable Create button if editing
  const updateDisabled = aboutData._id === null; // Disable Update button if creating
  const deleteDisabled = aboutData._id === null; // Disable Delete button if creating

  return (
    <div className="admin-section">
      <h2>About Management</h2>
      <select
        onChange={handleSelectChange}
        className="select-item"
        value={aboutData._id || "new"}
      >
        <option className="select-item__option" value="new">
          New About
        </option>
        {allAboutData.map(({ _id, identity }) => (
          <option className="select-item__option" key={_id} value={_id}>
            {identity}
          </option>
        ))}
      </select>
      <form onSubmit={(e) => e.preventDefault()} className="admin-form">
        <div className="form-group">
          <label>Clients</label>
          <input
            type="number"
            name="clients"
            placeholder="Clients"
            value={aboutData.clients || ""}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Projects</label>
          <input
            type="number"
            name="projects"
            placeholder="Projects"
            value={aboutData.projects || ""}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Work Experience (years)</label>
          <input
            type="number"
            name="workExperience"
            placeholder="Work Experience (years)"
            value={aboutData.workExperience || ""}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Current Status</label>
          <input
            type="text"
            name="currentStatus"
            placeholder="Current Status"
            value={aboutData.currentStatus || ""}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Identity</label>
          <input
            type="text"
            name="identity"
            placeholder="Identity"
            value={aboutData.identity || ""}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Image</label>
          {aboutData.image && typeof aboutData.image === "string" && (
            <img
              src={aboutData.image}
              alt="Current"
              className="img-preview"
              style={{ maxWidth: "100px", display: "block", margin: "10px 0" }}
            />
          )}
          {aboutData.image && aboutData.image instanceof File && (
            <p>Selected file: {aboutData.image.name}</p>
          )}
          <input type="file" name="image" onChange={handleChange} />
        </div>
        <div className="form-group full-width">
          <label>Description (English)</label>
          <textarea
            name="en"
            placeholder="Description in English"
            value={aboutData.description.en || ""}
            onChange={handleDescriptionChange}
            rows="6"
            required
          />
        </div>
        <div className="form-group full-width">
          <label>Description (French)</label>
          <textarea
            name="fr"
            placeholder="Description in French"
            value={aboutData.description.fr || ""}
            onChange={handleDescriptionChange}
            rows="6"
            required
          />
        </div>
        <div>
          <div className="btn-actions">
            <button
              className="btn btnPrimary"
              type="button"
              onClick={() => {
                if (
                  window.confirm("Are you sure you want to create this entry?")
                ) {
                  handleCreate();
                }
              }}
              disabled={createDisabled}
            >
              Create
            </button>
            <button
              className="btn btnSecondary"
              type="button"
              onClick={() => {
                if (
                  window.confirm("Are you sure you want to update this entry?")
                ) {
                  handleUpdate();
                }
              }}
              disabled={updateDisabled}
            >
              Update
            </button>
            <button
              className="btn btnDanger"
              type="button"
              onClick={() => {
                if (
                  window.confirm("Are you sure you want to delete this entry?")
                ) {
                  handleDelete();
                }
              }}
              disabled={deleteDisabled}
            >
              Delete
            </button>
          </div>
        </div>
      </form>
      {confirmationMessage && (
        <div className="confirmation-message">{confirmationMessage}</div>
      )}
    </div>
  );
};

export default AboutAdmin;
